
import React, { useContext, useEffect, useRef, useState } from 'react';
import DropDownV2 from '../../../../global/ui/DropDownV2';
import { MyContext } from '../../../../context';
import { emptyFunction, getCompanyName } from '../../../../utils';
import { makeGetCall, makeNextPageCall } from '../../../../effects/requests';
import { ApiResponseType } from '../../../../types';
import { endPoints } from '../../../../utils/endpoints';

interface LocationOption {
    label: string;
    value: string;
    id: any
}

interface GalleryImage {
    id: string;
    image: string;
    folder: string;
}

interface GalleryFolder {
    id: string;
    name: string;
    images: GalleryImage[];
}

interface Props {
    onBack: () => void;
    onAssign: (data: any) => void;
}

const FolderIcon = ({ active }: { active: boolean }) => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill={active ? '#6C3CE9' : '#F5C518'}
        stroke="none"
    >
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    </svg>
);

const BrowseLocationImages = ({ onBack, onAssign }: Props) => {
    const { userData } = useContext(MyContext);

    const [allLocationData, setAllLocationData] = useState<LocationOption[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
    const [nextUrl, setNextUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean | null>(false);

    const [allFolderData, setAllFolderData] = useState<GalleryFolder[]>([]);
    const [activeGallery, setActiveGallery] = useState<string | null>(null);
    const [selectedImageId, setSelectedImageId] = useState<string | null>(null);

    const folderRequestRef = useRef(0);

    const formatRes = (results: any) => {
        const data = (results || [])?.map((item: any) => ({
            ...item,
            label: item?.location?.name,
            value: item?.location?.id
        }));
        return data
    }

    // Fetch all locations
    useEffect(() => {
        if (!userData?.id) return;

        const params = {
            production_company_name: getCompanyName(userData),
            search: '',
            my_vault: false
        };

        makeGetCall(endPoints.LOCATION_VAULT, params)
            .then((res: ApiResponseType<any>) => {
                if (res?.status?.code === 200) {
                    const data = formatRes(res?.results)
                    setAllLocationData(data);
                    setNextUrl(res?.next || null);
                }
            })
            .catch(emptyFunction);
    }, [userData?.id]);

    // Fetch folders by selected location
    const fetchFolders = (locationId: string) => {
        const requestId = ++folderRequestRef.current;
        setAllFolderData([]);
        setActiveGallery(null);
        setSelectedImageId(null);

        makeGetCall(endPoints.LOCATION_FOLDER, {
            location_id: locationId
        })
            .then((res: ApiResponseType<any>) => {
                if (requestId !== folderRequestRef.current) return;

                if (res?.status?.code === 200) {
                    setAllFolderData(res?.results || []);
                }
            })
            .catch(emptyFunction);
    };

    // Location selection
    const handleLocationSelect = (location: LocationOption | null) => {
        setSelectedLocation(location);
        setAllFolderData([]);
        setActiveGallery(null);
        setSelectedImageId(null);
        if (location?.id) {
            fetchFolders(location?.id);
        } else {
            ++folderRequestRef.current;
        }
    };

    // Folder selection
    const handleFolderSelect = (folderId: string) => {
        setActiveGallery(folderId);
        setSelectedImageId(null);
    };

    // Get images from selected folder
    const selectedFolder = allFolderData.find((folder) => folder.id === activeGallery);

    const images = selectedFolder?.images || [];

    // Get selected image
    const selectedImage = images.find((img) => img.id === selectedImageId);

    // Assign selected image
    const handleAssign = () => {
        if (!selectedImage) return;
        onAssign(selectedImage);
    };

    const fetchNextPage = () => {
        if (!nextUrl) return;
        setIsLoading(true)
        makeNextPageCall(nextUrl, false, true).then((res: ApiResponseType<any>) => {
            setIsLoading(false);
            const data = formatRes(res?.results)
            setAllLocationData([...allLocationData, ...data]);
            setNextUrl(res?.next);
        }).catch(() => setIsLoading(false))
    }

    return (
        <>
            {/* Backdrop */}
            <div className="cl-backdrop" />

            {/* Drawer panel */}
            <div className="cl-drawer">

                {/* Header */}
                <div className="cl-drawer__header">
                    <button
                        className="cl-drawer__close"
                        onClick={onBack}
                    >
                        <img
                            src="/assets/images/dismiss.svg"
                            alt="close"
                        />
                    </button>

                    <h2 className="cl-drawer__title">
                        Browse Location Images
                    </h2>
                </div>

                {/* Step content */}
                <div className="cl-drawer__body">
                    <div className="cs-step">

                        {/* Select Location */}
                        <DropDownV2
                            label="Locations"
                            placeholder="Please select location"
                            data={allLocationData}
                            selectedItems={selectedLocation}
                            onSelect={handleLocationSelect}
                            enablePagination={true}
                            onLoadMore={fetchNextPage}
                            hasMore={nextUrl ? true : false}
                            isLoading={isLoading ? true : false}
                        />

                        {/* Select Galleries */}
                        {allFolderData?.length > 0 &&
                            <h3 className="cs-step__title cs-step__title--lg">
                                Select Galleries
                            </h3>
                        }
                        <div className="cs-gallery-grid">
                            {allFolderData.map((gallery) => {
                                const isActive = gallery.id === activeGallery;

                                return (
                                    <button
                                        type="button"
                                        key={gallery.id}
                                        className={`cs-gallery-card${isActive ? ' cs-gallery-card--active' : ''}`}
                                        onClick={() => handleFolderSelect(gallery.id)}
                                    >
                                        <FolderIcon active={isActive} />
                                        {gallery.name}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Select Image */}
                        {allFolderData?.length > 0 &&
                            <h3 className="cs-step__title cs-step__title--lg">
                                Select Image
                            </h3>
                        }

                        <div className="cs-image-grid">
                            {images.map((img) => {
                                const isSelected = img.id === selectedImageId;

                                return (
                                    <button
                                        type="button"
                                        key={img.id}
                                        className="cs-image-tile"
                                        onClick={() => setSelectedImageId(img.id)}
                                    >
                                        <img src={img.image} alt="" />

                                        <span
                                            className={`cs-image-tile__check${isSelected ? ' cs-image-tile__check--selected' : ''}`}
                                        >
                                            <svg
                                                width="12"
                                                height="12"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="3"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        </span>
                                    </button>
                                );
                            })}

                            {images.length === 0 && (
                                <p className="cs-empty-text">
                                    No images in this gallery yet.
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="cl-drawer__footer">
                    <div />
                    <button
                        className="cl-btn-next"
                        onClick={handleAssign}
                        disabled={!selectedImage}
                    >
                        Assign Image
                    </button>
                </div>
            </div>
        </>
    );
};

export default BrowseLocationImages;